import React from "react";
import { Mail, MessageCircle, BookOpen, ChevronDown } from "lucide-react";
import Nav from "@/components/Nav";
import SiteFooter from "@/components/SiteFooter";

const SUPPORT_EMAIL = "support@bookforge360.com";

// iter-76 — FAQ content. Static for now so it renders instantly on the
// public /support route (no DB round-trip, indexable by search engines).
// Add new Q&A here as authors report questions — a lightweight CMS is
// on the backlog if the list grows past ~20 entries.
const FAQS = [
  {
    q: "How do I format my first ebook?",
    a: "Sign up for a free account, then click 'Upload draft' on the Dashboard. Drop in a DOCX, PDF, TXT, or Markdown file — BookForge360 detects your chapters automatically, cleans up typography, and offers EPUB, PDF, and DOCX exports in seconds."
  },
  {
    q: "What counts as one book credit?",
    a: "One book credit is deducted on the first upload of a new manuscript OR on the first Save of a Manual Entry book. Re-uploading a corrected draft to the same book is free (up to 3 re-uploads). Exports (EPUB / PDF / DOCX) are always unlimited."
  },
  {
    q: "Can I re-upload a corrected draft without spending a credit?",
    a: "Yes — you get 3 free re-uploads per book. Re-uploads run the deterministic formatter only, they don't re-trigger the AI chapter-detection call, so we don't burn Claude credits during corrections."
  },
  {
    q: "How does the yearly discount work?",
    a: "Yearly plans are billed at 10× monthly — you save 2 months by paying annually. Author $190/yr (vs $228 monthly), Pro $490/yr (vs $588), Studio $1490/yr (vs $1788)."
  },
  {
    q: "What happens if I exceed my monthly book or hero-image credits?",
    a: "On paid plans (Author/Pro/Studio) overages don't cut you off — they're billed at your next monthly invoice. Author: +$1.99/book, +$0.30/image. Pro: +$1.49/book, +$0.20/image. Studio: +$0.99/book, +$0.15/image. Free plan hard-caps because there's no card on file."
  },
  {
    q: "Can I collaborate with teammates?",
    a: "Yes — every paid plan includes shared workspaces. Author gets 1 workspace + 2 seats. Pro: 3 workspaces + 10 seats. Studio: unlimited. Workspace admins can add books, lock chapters, and manage teammates."
  },
  {
    q: "Who can delete books in a shared workspace?",
    a: "Only the workspace admin (the person who created the workspace) can delete books from a shared workspace, even if a teammate uploaded the book. This protects the team's work from accidental deletion. Personal books (outside any workspace) can always be deleted by their owner."
  },
  {
    q: "Can I lock chapters so nobody accidentally edits them?",
    a: "Yes. In Manual Entry, click the lock icon on any chapter row. Locked chapters can't be renamed, moved, or deleted until unlocked. Use the 'Lock all chapters' button at the top of § Chapters to freeze a whole manuscript in one click."
  },
  {
    q: "How do AI hero images work?",
    a: "For every chapter, BookForge360 can auto-generate a title-page hero image using Gemini Nano Banana. Each generation counts against your monthly hero-image credits (Author 30, Pro 150, Studio 500). Images embed directly into your EPUB and PDF exports."
  },
  {
    q: "Is my manuscript private and secure?",
    a: "Yes. Every book is scoped to your user account (or your workspace's members). We never train AI models on your content, and we never share your manuscripts with third parties. Passwords are bcrypt-hashed, cookies are httpOnly + SameSite=lax, and every AI call is metered so no runaway costs."
  },
  {
    q: "How do I cancel my subscription?",
    a: "From your Dashboard, click the plan tier chip → Manage subscription. That opens the Stripe Customer Portal where you can cancel, upgrade, downgrade, or update your card. Cancellation takes effect at the end of your current billing period — no proration surprises."
  },
  {
    q: "Do you offer refunds?",
    a: "Yes — within 14 days of your first paid charge, no questions asked. Email support@bookforge360.com with your account email and we'll process it within 2 business days."
  },
];

function FaqItem({ q, a }) {
  return (
    <details className="group border-b border-[color:var(--border)] py-4" data-testid="faq-item">
      <summary className="list-none cursor-pointer flex items-start justify-between gap-4">
        <span className="text-base text-[color:var(--text)] font-medium pr-8">{q}</span>
        <ChevronDown className="w-4 h-4 mt-1 text-[color:var(--text-muted)] transition-transform group-open:rotate-180"/>
      </summary>
      <p className="mt-3 text-sm text-[color:var(--text-muted)] leading-relaxed">{a}</p>
    </details>
  );
}

export default function Support() {
  return (
    <div className="min-h-screen bg-[color:var(--canvas)]">
      <Nav/>
      <main className="max-w-4xl mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-medium text-[color:var(--text)]" data-testid="support-title">
            Support &amp; FAQ
          </h1>
          <p className="mt-4 text-base text-[color:var(--text-muted)] max-w-2xl mx-auto">
            Questions? Something not working? We answer every email personally within one business day.
          </p>
        </div>

        {/* Contact cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-16">
          <a href={`mailto:${SUPPORT_EMAIL}`}
             className="bg-white border border-[color:var(--border)] p-6 hover:border-[color:var(--primary)] transition-colors"
             data-testid="support-email-card">
            <Mail className="w-6 h-6 text-[color:var(--primary)] mb-3" strokeWidth={1.5}/>
            <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)] mb-1">Email</p>
            <p className="text-sm text-[color:var(--text)] font-medium">{SUPPORT_EMAIL}</p>
            <p className="mt-2 text-xs text-[color:var(--text-muted)]">Reply within 1 business day</p>
          </a>
          <div className="bg-white border border-[color:var(--border)] p-6">
            <MessageCircle className="w-6 h-6 text-[color:var(--primary)] mb-3" strokeWidth={1.5}/>
            <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)] mb-1">Priority</p>
            <p className="text-sm text-[color:var(--text)] font-medium">Pro &amp; Studio plans</p>
            <p className="mt-2 text-xs text-[color:var(--text-muted)]">Same-day reply on business days</p>
          </div>
          <div className="bg-white border border-[color:var(--border)] p-6">
            <BookOpen className="w-6 h-6 text-[color:var(--primary)] mb-3" strokeWidth={1.5}/>
            <p className="text-xs uppercase tracking-[0.2em] text-[color:var(--text-muted)] mb-1">Docs</p>
            <p className="text-sm text-[color:var(--text)] font-medium">In-app help tips</p>
            <p className="mt-2 text-xs text-[color:var(--text-muted)]">Look for the ⓘ icons throughout the editor</p>
          </div>
        </div>

        <h2 className="text-2xl font-serif text-[color:var(--text)] mb-6">Frequently asked questions</h2>
        <div className="bg-white border border-[color:var(--border)] px-8" data-testid="support-faq-list">
          {FAQS.map((f, i) => <FaqItem key={i} {...f}/>)}
        </div>

        <div className="mt-16 bg-white border border-[color:var(--border)] p-8 text-center">
          <p className="text-sm text-[color:var(--text-muted)] mb-3">Didn't find your answer?</p>
          <a href={`mailto:${SUPPORT_EMAIL}?subject=BookForge360%20support%20question`}
             className="inline-block px-6 py-3 bg-[color:var(--text)] text-white text-sm hover:bg-[color:var(--primary)]"
             data-testid="support-contact-cta">
            Email {SUPPORT_EMAIL}
          </a>
        </div>
      </main>
      <SiteFooter/>
    </div>
  );
}
